// services/rent.js — авансов данък за доход от наем по ЗДДФЛ (чиста функция).
//
// Нормативната основа е СВЕРЕНА ДОСЛОВНО в lex.bg на 25.08.2026, редакция
// „изм. ДВ, бр. 30 от 27.03.2026 г." (виж 01-Текущо/СПРАВКА-ЗДДФЛ-наем-чл55.md):
//   • чл. 31, ал. 1 — облагаем доход = придобитият наем − 10 на сто разходи;
//   • чл. 44, ал. 1 и 3 — когато ЛИЦЕТО само внася: данък 10 % върху
//     (облагаем доход − осигурителни вноски за своя сметка);
//   • чл. 44, ал. 4, изр. ВТОРО — когато платецът е предприятие/СОЛ, данъкът
//     е облагаемият доход по чл. 31 × 10 % (БЕЗ приспадане на вноски) и се
//     удържа при изплащането. (Изречението липсваше в справката — законът печели.)
//   • чл. 44, ал. 2 — при ≥ 50 % намалена работоспособност данък се дължи
//     едва след като облагаемият доход от всички източници от началото на
//     годината превиши прага (7920 лв.);
//   • чл. 65, ал. 12 и 13; чл. 67, ал. 1 и 2 — срокове и четвърто тримесечие;
//   • чл. 45, ал. 4, 5 и 8 — сметка за изплатени суми и служебна бележка.
//
// ОБХВАТ (решение на собственика): само облагаем наем на имот.
// TODO: земеделска земя (необлагаема по чл. 13, ал. 1, т. 24) и разпределение
// между съсобственици НЕ влизат в тази версия — нарочно, не по пропуск.

const { round2 } = require('./totals');
const holidays = require('./holidays');

// ---- Константи с източник (не заровени числа) ----

// чл. 31, ал. 1 ЗДДФЛ: „…придобитият доход се намалява с 10 на сто разходи."
const NORMATIVE_EXPENSE_RATE = 0.10;
// чл. 44, ал. 3 и ал. 4, изр. второ: „данъчна ставка 10 на сто".
const ADVANCE_TAX_RATE = 0.10;
// чл. 44, ал. 2: прагът в закона е „7920 лв." (текстът не е преномериран).
const DISABILITY_THRESHOLD_BGN = 7920;
// 7920 ÷ 1,95583 = 4049,4287…, закръглено до втория знак по чл. 13 ЗВЕРБ.
const DISABILITY_THRESHOLD_EUR = 4049.43;
// чл. 12, ал. 1 ЗВЕРБ — фиксираният курс, всичките пет знака.
const BGN_PER_EUR = 1.95583;

function dfmt(iso) {
  const p = String(iso || '').split('-');
  return p.length === 3 ? p[2] + '.' + p[1] + '.' + p[0] : String(iso || '');
}

// Законовият срок за внасяне (чл. 65, ал. 12; чл. 67, ал. 1): краят на
// месеца, следващ тримесечието. Q4 — 31 януари на СЛЕДВАЩАТА година, и то
// само при писмено желание по чл. 65, ал. 13.
function quarterDeadlineBase(quarter, year) {
  if (quarter === 1) return year + '-04-30';
  if (quarter === 2) return year + '-07-31';
  if (quarter === 3) return year + '-10-31';
  return (year + 1) + '-01-31';
}

// Авансовият данък за едно тримесечие. Входът е обект (виж тестовете);
// изходът носи и notes[] — човешки изречения за екрана и досието.
function rentAdvanceTax(input) {
  const inp = input || {};
  const notes = [];
  const year = Number(inp.year) || new Date().getFullYear();
  const quarter = Math.min(4, Math.max(1, Number(inp.quarter) || 1));
  // От 01.01.2026 сумите са в евро; периоди до 31.12.2025 се декларират в
  // ЛЕВОВЕ, макар срокът да е през 2026 г. (разяснение на НАП; ЗВЕРБ чл. 11–13).
  const currency = year >= 2026 ? 'EUR' : 'BGN';
  if (currency === 'BGN') {
    notes.push('Период до 31.12.2025 г. — сумите и декларацията са в ЛЕВОВЕ, макар срокът да е през 2026 г. (разяснение на НАП; ЗВЕРБ, чл. 11–13).');
  }

  // Случаите, които калкулаторът НЕ смята — само разпознава и предупреждава.
  if (inp.foreignPerson) {
    return {
      special: 'foreign', currency, quarter, year, tax: null,
      notes: notes.concat('Наемодателят е ЧУЖДЕСТРАННО физическо лице — дължи се ОКОНЧАТЕЛЕН данък 10 % по чл. 37, ал. 1, т. 4 ЗДДФЛ (върху брутния наем), а не авансов по чл. 44. Този калкулатор не смята този случай.'),
    };
  }
  if (inp.condominium) {
    return {
      special: 'condominium', currency, quarter, year, tax: null,
      notes: notes.concat('Имот в режим на ЕТАЖНА СОБСТВЕНОСТ с общо събрание: доходът, платен от предприятие/СОЛ, се облага с ОКОНЧАТЕЛЕН данък по чл. 38, ал. 12 ЗДДФЛ, а не с авансов по чл. 44. Този калкулатор не смята този случай.'),
    };
  }

  const grossRent = round2(inp.grossRent);
  const payerIsEnterprise = !!inp.payerIsEnterprise;
  const normativeExpenses = round2(grossRent * NORMATIVE_EXPENSE_RATE);
  const taxableIncome = round2(grossRent - normativeExpenses);

  // База: чл. 44, ал. 4, изр. второ — платецът смята върху ЦЕЛИЯ облагаем
  // доход; чл. 44, ал. 1/ал. 3 — лицето приспада осигуровките за своя сметка.
  const contributions = round2(inp.healthContributions);
  let taxBase;
  if (payerIsEnterprise) {
    taxBase = taxableIncome;
    if (contributions > 0) {
      notes.push('При удържане от платеца данъкът се смята върху облагаемия доход БЕЗ приспадане на осигурителни вноски (чл. 44, ал. 4, изр. второ) — вноските се отчитат при годишното изравняване.');
    }
  } else {
    taxBase = round2(Math.max(0, taxableIncome - contributions));
  }

  // чл. 44, ал. 2: при ≥ 50 % намалена работоспособност данък се дължи само
  // след превишаване на прага — и само върху превишението за това плащане.
  if (inp.reducedCapacity) {
    const threshold = currency === 'EUR' ? DISABILITY_THRESHOLD_EUR : DISABILITY_THRESHOLD_BGN;
    const ytd = round2(inp.ytdTaxableIncome);
    const totalYtd = round2(ytd + taxableIncome);
    if (totalYtd <= threshold) {
      taxBase = 0;
      notes.push('Намалена работоспособност ≥ 50 % — авансов данък се дължи едва след като облагаемият доход от всички източници от началото на годината превиши ' + threshold.toFixed(2).replace('.', ',') + ' (чл. 44, ал. 2). Прагът не е достигнат — данък не се дължи. Лицето удостоверява ТЕЛК/НЕЛК пред платеца (чл. 44, ал. 5).');
    } else {
      const excess = round2(totalYtd - threshold);
      if (excess < taxBase) {
        taxBase = excess;
        notes.push('Намалена работоспособност ≥ 50 % — прагът по чл. 44, ал. 2 (' + threshold.toFixed(2).replace('.', ',') + ') се превишава с това плащане: данък се дължи само върху превишението.');
      }
    }
  }

  // Четвърто тримесечие: чл. 65, ал. 13 (платец) / чл. 67, ал. 2 (лицето).
  let withholder = payerIsEnterprise ? 'payer' : 'person';
  let tax = round2(taxBase * ADVANCE_TAX_RATE);
  let dueDateBase = quarterDeadlineBase(quarter, year);
  if (quarter === 4) {
    if (payerIsEnterprise && inp.wantsQ4Withholding) {
      notes.push('Четвърто тримесечие: лицето писмено е декларирало желание за удържане — платецът внася данъка до 31 януари на следващата година (чл. 65, ал. 13).');
    } else if (payerIsEnterprise) {
      tax = 0; withholder = 'none'; dueDateBase = null;
      notes.push('Четвърто тримесечие — данък не се удържа и не се внася, освен ако лицето писмено декларира, че желае удържане (чл. 65, ал. 13).');
    } else {
      tax = 0; withholder = 'none'; dueDateBase = null;
      notes.push('Четвърто тримесечие — лицето не внася авансов данък, без изключение (чл. 67, ал. 2).');
    }
  }
  // Срок в неприсъствен ден → следващият работен (чл. 22 ДОПК — правилото е
  // отбелязано като непотвърдено в справката, затова бележката казва да се
  // провери при подаване). Броенето на празници е САМО в services/holidays.js.
  let dueDate = dueDateBase;
  if (dueDateBase && !holidays.isWorkingDay(dueDateBase, inp.overrides)) {
    dueDate = holidays.nextWorkingDay(dueDateBase, inp.overrides);
    notes.push('Срокът ' + dfmt(dueDateBase) + ' е неприсъствен ден — мести се на следващия работен (' + dfmt(dueDate) + ') по чл. 22 ДОПК. Проверете при подаване.');
  }

  if (payerIsEnterprise) {
    notes.push('Платецът издава сметка за изплатени суми (образец 3а) при всяко изплащане, а при поискване — служебна бележка в 14-дневен срок (чл. 45, ал. 4, 5 и 8).');
  } else {
    notes.push('Лицето само определя и внася данъка (чл. 44, ал. 3) и само подава декларацията по чл. 55, ал. 2.');
  }

  // За изплащане на наемодателя: брутото минус УДЪРЖАНОТО от платеца.
  const netPayable = round2(grossRent - (withholder === 'payer' ? tax : 0));

  return {
    grossRent, normativeExpenses, taxableIncome, taxBase, tax, netPayable,
    withholder, dueDate, dueDateBase, currency, quarter, year, notes,
  };
}

module.exports = {
  rentAdvanceTax,
  NORMATIVE_EXPENSE_RATE,
  ADVANCE_TAX_RATE,
  DISABILITY_THRESHOLD_BGN,
  DISABILITY_THRESHOLD_EUR,
  BGN_PER_EUR,
};
